/**
 * Shared time entry and display. Pickers emit 24-hour `HH:MM`; the daily-log
 * backend normalizes saved clocks to AM/PM text. Display must read both.
 * Typed entry also accepts `7:52p`, `19:52`, or `752`. Quarter-hour suggestions
 * remain available to embedded fields; the full sheet supports every minute.
 */
import { parseProcoreClockValue } from "./procoreDailyLog";

export const TIME_STEP_MINUTES = 15;

/** Read both picker HH:MM values and the backend's normalized AM/PM values. */
export function parseStoredTime(value: string): { hours: number; minutes: number } | null {
  // Older imported records can also carry seconds; the UI edits minutes.
  const clock = value.trim().replace(/^(\d{1,2}:\d{2}):[0-5]\d$/, "$1");
  const parsed = parseProcoreClockValue(clock);
  return parsed ? { hours: parsed.hour, minutes: parsed.minute } : null;
}

/**
 * What a person actually types. Deliberately forgiving — `7:52 PM`, `7:52p`,
 * `752pm`, `19:52`, `7 pm` — because a field that rejects `7pm` teaches the
 * reader to distrust it, and every one of these has exactly one reading.
 *
 * Bare digits are 24-hour (`19:52`, `0730`); an am/pm suffix makes them
 * 12-hour, with `12am` → `00:00` and `12pm` → `12:00`.
 */
export function parseTypedTime(typed: string): string | null {
  const text = typed.trim().toLowerCase();
  if (!text) return "";
  const match = /^(\d{1,2})(?::?(\d{2}))?\s*(a|p|am|pm)?$/.exec(text);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = match[2] === undefined ? 0 : Number(match[2]);
  const suffix = match[3]?.[0];
  if (minutes > 59) return null;

  if (suffix) {
    if (hours < 1 || hours > 12) return null;
    if (suffix === "a") hours = hours === 12 ? 0 : hours;
    else hours = hours === 12 ? 12 : hours + 12;
  } else if (hours > 23) {
    return null;
  }

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** `"19:52"` → `"7:52 PM"`. The field's own reading voice, not the mono meta
 *  line's `7:52p` — this is a value a person is about to edit. */
export function displayTime(value: string): string | null {
  const parsed = parseStoredTime(value);
  if (!parsed) return null;
  const suffix = parsed.hours < 12 ? "AM" : "PM";
  const twelve = parsed.hours % 12 || 12;
  return `${twelve}:${String(parsed.minutes).padStart(2, "0")} ${suffix}`;
}

/** Every quarter hour of the day, as stored values. */
function timeSteps(): string[] {
  const steps: string[] = [];
  for (let minute = 0; minute < 24 * 60; minute += TIME_STEP_MINUTES) {
    steps.push(
      `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`,
    );
  }
  return steps;
}

export const TIME_STEPS = timeSteps();
